import { http } from '@/api/http'
import { toListParams, type ListQuery } from '@/api/query'
import { i18n } from '@/i18n'
import type {
  ContentLocale,
  ListResponse,
  ProcurementBatchStatus,
  ProcurementMovement,
  ProcurementMovementFilterOptions,
  ProcurementTransportJumpPreview,
} from '@/types/api'
import type { RequestOptions } from '../crud'
import { reportLanguage } from './reports.service'
import type { ProcurementTransportUnitStageChargeInput, ProcurementTransportUnitTrailerInput } from './transportUnits.service'

export type ProcurementMovementItemInput = {
  source_content_id: string
  moved_weight: string
  moved_package_count: string
  moved_front_count?: string
  moved_rear_count?: string
  target_package_id?: string
}

export type ProcurementMovementHeadInput = {
  carrier_id?: string
  vehicle_number: string
  driver_name?: string
  assigned_at?: string
  note?: string
  is_active?: boolean
}

export type CreateProcurementMovementInput = {
  idempotency_key: string
  movement_date: string
  land_vehicle_type: 'truck' | 'wagon'
  trailer_number: string
  location_id: string
  note?: string
  /** Older single-head form; heads lists every tractor with one active. */
  head?: ProcurementMovementHeadInput
  heads?: ProcurementMovementHeadInput[]
  /** Every trailer of the new truck with one active; wins over trailer_number. */
  trailers?: ProcurementTransportUnitTrailerInput[]
  items: ProcurementMovementItemInput[]
  stage_charges?: ProcurementTransportUnitStageChargeInput[]
}

/**
 * A «Перевалка» edit. Omitted header fields stay. Each item sets its source
 * line's new moved total: a line already moved changes, "0" returns it to its
 * container, and a line of a container already in the movement is added.
 */
export type UpdateProcurementMovementInput = {
  idempotency_key: string
  movement_date?: string
  trailer_number?: string
  note?: string
  /** «№ состава», a wagon's only: every wagon of its Перевалка takes it. */
  consist_number?: string
  heads?: ProcurementMovementHeadInput[]
  /** Replaces the truck's trailers (one active); trailer_number follows the active one. */
  trailers?: ProcurementTransportUnitTrailerInput[]
  items?: ProcurementMovementItemInput[]
}

/**
 * A wagon Перевалка (ALFI TEST #5): several wagons, each with its number and
 * its goods; the header, the tractor and one logistics sum per stage (split
 * over the wagons by kg) are shared.
 */
export type CreateProcurementWagonMovementInput = {
  idempotency_key: string
  movement_date: string
  location_id: string
  note?: string
  /** «№ состава»: the train's number, shared by every wagon. */
  consist_number: string
  heads?: ProcurementMovementHeadInput[]
  stage_charges?: ProcurementTransportUnitStageChargeInput[]
  wagons: { wagon_number: string; items: ProcurementMovementItemInput[] }[]
}

/** One more wagon for a saved wagon Перевалка: its number and goods; the rest is the Перевалка's. */
export type AddProcurementWagonInput = {
  idempotency_key: string
  movement_date: string
  note?: string
  wagon_number: string
  items: ProcurementMovementItemInput[]
}

export type WagonGroupMove = { target_proforma_status_id: string; target_proforma_substatus_id: string; reason?: string }

const base = '/procurement/movements'
const groups = `${base}/wagon-groups`

export const procurementMovementsService = {
  path: 'procurement/movements',
  async list(query?: ListQuery, options?: RequestOptions) {
    const { data } = await http.get<ListResponse<ProcurementMovement>>(base, {
      params: toListParams(query),
      ...options,
    })
    return { count: data.count ?? 0, items: data.items ?? [] }
  },
  async get(id: string, options?: RequestOptions) {
    const { data } = await http.get<ProcurementMovement>(`${base}/${encodeURIComponent(id)}`, options)
    return data
  },
  async create(body: CreateProcurementMovementInput) {
    const { idempotency_key, ...command } = body
    const { data } = await http.post<ProcurementMovement>(base, command, {
      headers: { 'Idempotency-Key': idempotency_key },
    })
    return data
  },
  /** Creates every wagon of a wagon Перевалка at once; returns the wagons' movements. */
  async createWagons(body: CreateProcurementWagonMovementInput) {
    const { idempotency_key, ...command } = body
    const { data } = await http.post<ProcurementMovement[]>(`${base}/wagons`, command, {
      headers: { 'Idempotency-Key': idempotency_key },
    })
    return data
  },
  /**
   * Adds a wagon to a saved wagon Перевалка: it joins the wagons' status and the
   * logistics still planned are split over all the wagons by kg again. Returns every wagon.
   */
  async addWagon(groupId: string, body: AddProcurementWagonInput) {
    const { idempotency_key, ...command } = body
    const { data } = await http.post<ProcurementMovement[]>(`${groups}/${encodeURIComponent(groupId)}/wagons`, command, {
      headers: { 'Idempotency-Key': idempotency_key },
    })
    return data
  },
  /** The status the wagons of a wagon Перевалка share and where they can all move. */
  async wagonGroupStatus(groupId: string, options?: RequestOptions) {
    const { data } = await http.get<ProcurementBatchStatus>(`${groups}/${encodeURIComponent(groupId)}/status`, options)
    return data
  },
  async previewWagonGroupJump(groupId: string, body: WagonGroupMove, options?: RequestOptions) {
    const { data } = await http.post<ProcurementTransportJumpPreview>(`${groups}/${encodeURIComponent(groupId)}/status-transitions/preview`, body, {
      params: { lang: reportLanguage(i18n.locale) },
      ...options,
    })
    return data
  },
  async moveWagonGroup(groupId: string, direction: 'forward' | 'reverse', body: WagonGroupMove) {
    const { data } = await http.post<ProcurementBatchStatus>(`${groups}/${encodeURIComponent(groupId)}/status-transitions/${direction}`, body)
    return data
  },
  async update(id: string, body: UpdateProcurementMovementInput) {
    const { idempotency_key, ...command } = body
    const { data } = await http.patch<ProcurementMovement>(`${base}/${encodeURIComponent(id)}`, command, {
      headers: { 'Idempotency-Key': idempotency_key },
    })
    return data
  },
  async filterOptions(options?: RequestOptions) {
    const { data } = await http.get<ProcurementMovementFilterOptions>(`${base}/filter-options`, options)
    return data
  },
  /** Names in the file follow the content locale. */
  async export(query: ListQuery, locale: ContentLocale) {
    const { data } = await http.post<Blob>(
      `${base}/export`,
      { search: query.search ?? '', filters: query.filters ?? [], sort: query.sort ?? [], lang: reportLanguage(locale) },
      { responseType: 'blob' },
    )
    return data
  },
}
